import { formatStatusLabel } from '@/ui';
import type { UiTone } from '@/theme/uiTokens';
import type { BuildDto, BuildStatus, PipelineBuildSummaryDto } from '@flowqa/shared';

export type PipelineStatusChartSeries = {
  key: BuildStatus;
  label: string;
  colorKey: UiTone;
};

export type PipelineStatusChartData = {
  data: Array<Record<string, string | number>>;
  series: PipelineStatusChartSeries[];
};

const allBuildStatuses: BuildStatus[] = ['completed', 'failed', 'running', 'queued'];

const getSeriesColorKey = (status: BuildStatus): UiTone => {
  if (status === 'completed') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'running') return 'blue';
  return 'neutral';
};

const resolveVisibleBuildStatuses = (selectedStatuses: BuildStatus[]): BuildStatus[] => {
  if (selectedStatuses.length === 0) return allBuildStatuses;
  return allBuildStatuses.filter((status) => selectedStatuses.includes(status));
};

const buildSeries = (visibleStatuses: BuildStatus[]): PipelineStatusChartSeries[] =>
  visibleStatuses.map((status) => ({
    key: status,
    label: formatStatusLabel(status),
    colorKey: getSeriesColorKey(status),
  }));

const toDayKey = (build: BuildDto): string | null => {
  const timestamp = build.startedAt ?? build.finishedAt;
  if (!(timestamp instanceof Date) || Number.isNaN(timestamp.getTime())) return null;
  return timestamp.toISOString().slice(0, 10);
};

export const buildPipelineStatusChartDataFromSummary = (
  counts: Partial<Record<BuildStatus, number>>,
  label: string,
  selectedStatuses: BuildStatus[],
): PipelineStatusChartData => {
  const visibleStatuses = resolveVisibleBuildStatuses(selectedStatuses);
  return {
    data: [
      visibleStatuses.reduce<Record<string, string | number>>(
        (acc, status) => ({
          ...acc,
          [status]: counts[status] ?? 0,
        }),
        { label },
      ),
    ],
    series: buildSeries(visibleStatuses),
  };
};

export const buildPipelineStatusChartDataFromBuilds = (
  builds: BuildDto[],
  chartDates: string[],
  selectedStatuses: BuildStatus[],
): PipelineStatusChartData => {
  const visibleStatuses = resolveVisibleBuildStatuses(selectedStatuses);
  const countsByDay = new Map<string, Record<BuildStatus, number>>();

  builds.forEach((build) => {
    const dayKey = toDayKey(build);
    if (!dayKey) return;
    const existing = countsByDay.get(dayKey) ?? {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };
    existing[build.status] += 1;
    countsByDay.set(dayKey, existing);
  });

  const dates = chartDates.length > 0 ? chartDates : Array.from(countsByDay.keys()).sort();
  const data = dates.map((date) => {
    const counts = countsByDay.get(date) ?? {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };
    return visibleStatuses.reduce<Record<string, string | number>>(
      (acc, status) => ({
        ...acc,
        [status]: counts[status],
      }),
      { label: date },
    );
  });

  return { data, series: buildSeries(visibleStatuses) };
};

export const buildPipelineStatusChartDataFromBuildSummary = (
  summary: PipelineBuildSummaryDto | null,
  selectedStatuses: BuildStatus[],
): PipelineStatusChartData => {
  const visibleStatuses = resolveVisibleBuildStatuses(selectedStatuses);
  const data = (summary?.buckets ?? []).map((bucket) =>
    visibleStatuses.reduce<Record<string, string | number>>(
      (acc, status) => ({
        ...acc,
        [status]: bucket.statusCounts[status],
      }),
      { label: bucket.day.toISOString().slice(0, 10) },
    ),
  );
  return { data, series: buildSeries(visibleStatuses) };
};
